import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import { MainContext } from '../../../context/main.context';
import { ISelectOptions, ISendLetterSupport, IWebAuthData } from '../../../interfaces';
import { getAuthElemStyle, getUserAuthData, isEmail, setFocus } from '../../../common/tools';
import Header from '../../header';
import Footer from '../../footer';
import { getUserPayment, sendLetterSupport } from '../../../stores/payment.store';
import { GET_PROFILE_INFO_ERROR } from '../../../common/errorCodes';
import ManualController from '../../../hoc/ManualController';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../../modal/Modal';
import { IsOkSvg } from '../../../icons';


export default function TransactionHelper() {
    const [ searchParams ] = useSearchParams();
    const { manualRedirect } = ManualController();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { paymentId, settings, modalContent, userAuthData, setModalContent, toggleLoader } = useContext(MainContext);
    const [ userData, setUserData ] = useState<IWebAuthData | null>(null);
    const [ email, setEmail ] = useState<string>('');
    const [ emailErr, setEmailErr ] = useState<string>('');
    const [ selectedOption, setSelectedOption ] = useState<string>('');
    const [ isDisableBtn, setDisableBtn ] = useState<boolean>(true);
    const [ showRequestModal, setShowRequestModal ] = useState<boolean>(false);
    const id: string | null = searchParams.get('id') || paymentId;

    const OPTIONS: ISelectOptions[] = [
        { value: 'Не начислен дроп', label: t('support.dropNotCredited') },
        { value: 'Другое', label: t('support.other') },
    ];

    useEffect((): void => {
        if (!id) {
            return;
        }

        const webAuthData: IWebAuthData | null = getUserAuthData();

        if (!userAuthData || !webAuthData) {
            navigate('/');
            return;
        }

        setUserData(webAuthData);
        _getUserPayment();
    }, [ id ]);

    useEffect(() => {
        if (selectedOption && email) {
            setDisableBtn(false);
        } else {
            setDisableBtn(true);
        }
    }, [ selectedOption, email ]);

    const _getUserPayment = () => {
        toggleLoader(true);

        getUserPayment(id as string).then(res => {
            const result: boolean = res?.data?.result;

            if (!result) {
                navigate('/transactions');
                return;
            }
        }, err => {
            const errData = err?.response?.data || err;
            manualRedirect(errData, 'auth', true, GET_PROFILE_INFO_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    const _onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        if (!email) {
            setFocus('help-email-id');
            setEmailErr(t('login.enterEmail'));
            return;
        }

        if (!isEmail(email)) {
            setFocus('req-email-id');
            setEmailErr(t('login.invalidEmail'));
            return;
        }

        if (emailErr) {
            setEmailErr('');
        }

        const data: ISendLetterSupport = {
            nickName: userData?.nickName,
            currentAuthType: userData?.currentAuthType,
            email,
            reason: selectedOption,
            paymentId: id,
        }

        _sendLetterSupport(data);
    }

    const _sendLetterSupport = (data: ISendLetterSupport) => {
        toggleLoader(true);

        sendLetterSupport(data).then(res => {
            const result: boolean = res?.data?.result;

            if (!result) {
                toast.error(t('payment.status.fail'));
                return;
            }

            setShowRequestModal(true);
        }, err => {
            const errData = err?.response?.data || err;
            console.error({errData})

            toast.error(t('payment.status.fail'));
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    const _onCloseModal = () => {
        setShowRequestModal(false);

        if (modalContent) {
            setModalContent(null);
            return;
        }

        navigate('/');
    }

    const _renderBody = () => {
        return (
            <div className="auth-modal-menu">
                <div className="login-modal-header">
                    <div className="login-modal-header-title text-overflow">
                        {t('support.contactSupport')}
                    </div>
                </div>
                <form
                    noValidate
                    className="auth-modal-menu-body req-auth-modal-menu-body"
                    onSubmit={(e) => _onSubmit(e)}
                >
                    <div className="login-modal-menu-body-input">
                        <div className="login-modal-menu-body-input-title">
                            {t('support.paymentId')}
                        </div>
                        <div className="login-modal-menu-body-input-title-value">
                            {id}
                        </div>
                    </div>
                    <div className="login-modal-menu-body-input">
                        <div className="login-modal-menu-body-input-title">
                            {t('support.userId')}
                        </div>
                        <div className="login-modal-menu-body-input-title-value">
                            {userData?.userId}
                        </div>
                    </div>
                    <div className="login-modal-menu-body-input">
                        <div className="login-modal-menu-body-input-title">
                            {t('support.requestReason')}
                        </div>
                        {
                            OPTIONS.map((option: ISelectOptions) => (
                                <label key={option.value} style={{display: 'flex'}}>
                                    <input
                                        type="radio"
                                        value={option.value}
                                        checked={selectedOption === option.value}
                                        onChange={(e) => setSelectedOption(e.target.value)}
                                    />
                                    <div className="login-modal-menu-body-input-title-value">
                                        {option.label}
                                    </div>
                                </label>
                            ))
                        }
                    </div>
                    <div className="login-modal-menu-body-input">
                        <div className="login-modal-menu-body-input-title">
                            {t('support.feedbackEmail')}
                        </div>
                        <input
                            className="login-modal-menu-body-input-item"
                            type="email"
                            id="help-email-id"
                            autoComplete="off"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{border: `1px solid ${emailErr ? '#B01E1E' : '#d1d1d0'}`}}
                        />
                        { emailErr ? <div className="req-input-err">{emailErr}</div> : null }
                    </div>
                    <button
                        className="btn"
                        type="submit"
                        disabled={isDisableBtn}
                        style={{
                            background: isDisableBtn ? '#D1D1D0' : '#2368a2',
                            cursor: isDisableBtn ? 'auto' : 'pointer',
                        }}
                    >
                        {t('login.send')}
                    </button>
                </form>
            </div>
        );
    }

    const _renderRequestAcceptedModal = () => {
        return (
            <div className="transaction-modal">
                <div className="transaction-modal-title">
                    <div className="transaction-modal-title-img">
                        <IsOkSvg/>
                    </div>
                    <div>
                        {t('support.success')}
                    </div>
                </div>
                <div className="transaction-modal-text">
                    {t('support.successText')}
                </div>
                    <button className="transaction-modal-button" onClick={_onCloseModal}>
                        OK
                    </button>
            </div>
        );
    }

    return (
        <div className="auth" id="auth-element-id" style={{background: !modalContent ? '#e8e8e8' : 'unset'}}>
            <div
                className="auth-container"
                style={{
                    background: !modalContent ? '#e8e8e8' : 'unset',
                    minHeight: !modalContent ? 'calc(100vh - 100px)' : '100%',
                }}
            >
                <div className="auth-modal" style={getAuthElemStyle(settings)}>
                    <Header origin="transactionHelper" userData={userData}/>
                    {_renderBody()}
                </div>
            </div>
            <Modal hideCloseButton open={showRequestModal}>
                {_renderRequestAcceptedModal()}
            </Modal>
            {!modalContent && <Footer/>}
        </div>
    );
}